import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AlertCircle, ArrowLeft, Clock, MapPin, MessageSquare, Pencil, Trash2, X } from "lucide-react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import StatusChip from "@/components/shared/StatusChip";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { formatDate } from "@/utils/formatters";
import { useStudentWorkLog, useDeleteStudentWorkLog } from "@/hooks/useStudentData";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

export const Route = createFileRoute("/student/work-logs/$id")({ component: WorkLogDetail });

function WorkLogDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: log, isLoading } = useStudentWorkLog(id);
  const deleteLog = useDeleteStudentWorkLog();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  // The error notice hides itself after six seconds, like the snackbar it replaces.
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(""), 6000);
    return () => clearTimeout(t);
  }, [error]);

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!log) {
    return (
      <EmptyState
        title="Work log not found"
        description="This entry may have been removed or you may not have access to it."
        actionLabel="Back to list"
        onAction={() => navigate({ to: "/student/work-logs" })}
      />
    );
  }

  // Only an entry a partner has not acted on yet can still be changed. Once it
  // is approved or rejected it is the record both sides rely on for credit.
  const isEditable = log.status === "pending" || log.status === "changes_requested";
  const partnerName = log.partner_name || log.partnerName || "—";
  const description = log.description || log.content || "";
  const challenges = log.challenges || log.challenge || "";

  const handleDelete = async () => {
    setConfirmOpen(false);
    try {
      await deleteLog.mutateAsync(String(log.id));
      navigate({ to: "/student/work-logs" });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "This work log could not be deleted.";
      setError(message);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Work log detail"
        subtitle={`Log entry for ${formatDate(log.date)}`}
        breadcrumbs={[
          { label: "Dashboard", path: "/student/dashboard" },
          { label: "Work Logs", path: "/student/work-logs" },
          { label: formatDate(log.date) },
        ]}
      />

      {error && (
        <Alert variant="destructive" aria-live="assertive">
          <AlertCircle />
          <AlertDescription className="flex-1 text-foreground">{error}</AlertDescription>
          <button
            type="button"
            onClick={() => setError("")}
            aria-label="Dismiss"
            className="-my-0.5 rounded-xs p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </Alert>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {/* Main content */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="min-w-0"
        >
          <section className="rounded-md border border-border bg-card p-4 md:p-6">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-display-sm font-normal text-foreground">
                  {formatDate(log.date)}
                </h2>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="size-4" aria-hidden />
                  {partnerName}
                </p>
              </div>
              <StatusChip status={log.status} />
            </div>

            <dl className="mb-6 grid grid-cols-2 gap-3 border-t border-border pt-5 sm:grid-cols-3">
              <div className="rounded-md border border-border bg-background p-3">
                <dt className="eyebrow mb-1 flex items-center gap-1.5">
                  <Clock className="size-3.5" aria-hidden />
                  Total hours
                </dt>
                <dd className="text-base text-foreground tabular-nums">{log.hours} hours</dd>
              </div>
              <div className="rounded-md border border-border bg-background p-3">
                <dt className="eyebrow mb-1">Partner</dt>
                <dd className="truncate text-sm text-foreground">{partnerName}</dd>
              </div>
              <div className="col-span-2 rounded-md border border-border bg-background p-3 sm:col-span-1">
                <dt className="eyebrow mb-1">Placement</dt>
                <dd className="truncate text-sm text-foreground">{log.slot_title || "—"}</dd>
              </div>
            </dl>

            <div className="mb-6">
              <h3 className="mb-2 text-sm font-medium text-foreground">Tasks description</h3>
              <p className="text-sm leading-7 whitespace-pre-line text-body">
                {description || "No description was provided."}
              </p>
            </div>

            {challenges && (
              <div>
                <h3 className="mb-2 text-sm font-medium text-foreground">Challenges faced</h3>
                <p className="text-sm leading-7 whitespace-pre-line text-body">{challenges}</p>
              </div>
            )}
          </section>
        </motion.div>

        {/* Sidebar */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.08 }}
          className="flex min-w-0 flex-col gap-4"
        >
          <SectionCard title="Actions">
            <div className="flex flex-col gap-2">
              {isEditable ? (
                <Button
                  onClick={() =>
                    navigate({
                      to: "/student/work-logs/edit/$id",
                      params: { id: String(log.id) },
                    })
                  }
                  className="w-full"
                >
                  <Pencil />
                  Edit work log
                </Button>
              ) : (
                <p className="mb-2 text-sm text-muted-foreground">
                  This log has been reviewed, so it can no longer be changed.
                </p>
              )}

              {isEditable && (
                <Button
                  variant="destructive-outline"
                  className="w-full"
                  onClick={() => setConfirmOpen(true)}
                  disabled={deleteLog.isPending}
                >
                  <Trash2 />
                  {deleteLog.isPending ? "Deleting..." : "Delete work log"}
                </Button>
              )}

              <Button variant="outline" className="w-full" asChild>
                <Link to="/student/work-logs">
                  <ArrowLeft />
                  Back to work logs
                </Link>
              </Button>
            </div>
          </SectionCard>

          {log.feedback && (
            <SectionCard
              title="Supervisor feedback"
              actions={<MessageSquare className="size-4 text-muted-foreground" aria-hidden />}
            >
              <p className="text-sm leading-7 whitespace-pre-line text-body">{log.feedback}</p>
            </SectionCard>
          )}
        </motion.div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this work log?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the entry for {formatDate(log.date)} and its {log.hours} hours. It
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className={buttonVariants({ variant: "destructive" })}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
